import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router } from '@angular/router';
import { appRoutes } from './app.routes';
import { HttpBackend, provideHttpClient } from '@angular/common/http';
import { APP_TYPE, EnvHelper, logger, TenantStore } from '@frontend/shared';
import * as Sentry from '@sentry/angular';
import { replayIntegration } from '@sentry/browser';
import { provideTranslateService, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { provideToastr } from 'ngx-toastr';
import { provideMapboxGL } from 'ngx-mapbox-gl';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { firstValueFrom } from 'rxjs';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
logger.setLogLevel((window as any).spaConfig?.log_level || 'warn');

async function initialiseTranslations(translate: TranslateService): Promise<void> {
  translate.addLangs(['de', 'en']);

  const browserLang = translate.getBrowserLang() ?? 'de';
  const langToUse = translate.getLangs().includes(browserLang) ? browserLang : 'de';

  await firstValueFrom(translate.use(langToUse));
}

function TranslationLoaderFactory(http: HttpBackend) {
  return new MultiTranslateHttpLoader(http, ['/i18n/', '/i18n/shared/']);
}

const environment = EnvHelper.getWebEnv();
Sentry.init({
  dsn: 'https://2e31ed65899e2322ef42015138fb5235@o4509212613869568.ingest.de.sentry.io/4509269820178512',
  environment,
  enabled: environment !== 'local',
  integrations: [Sentry.browserTracingIntegration(), replayIntegration()],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideTranslateService({
      fallbackLang: 'de',
      loader: {
        provide: TranslateLoader,
        useFactory: TranslationLoaderFactory,
        deps: [HttpBackend],
      },
    }),
    provideAnimations(),
    provideRouter(appRoutes),
    provideToastr({
      timeOut: 3000,
      positionClass: 'toast-bottom-full-width',
      preventDuplicates: true,
      closeButton: true,
      progressBar: true,
    }),
    provideHttpClient(),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      // Initialize Sentry TraceService
      inject(Sentry.TraceService);
    }),
    provideAppInitializer(() => initialiseTranslations(inject(TranslateService))),
    provideAppInitializer(async () => {
      try {
        await inject(TenantStore).init();
      } catch (error) {
        // Log error but don't block app initialization
        // The error will be stored in ErrorStore and displayed by the app
        console.error('Failed to initialize tenant store:', error);
      }
    }),
    { provide: APP_TYPE, useValue: 'client' },
    provideMapboxGL({
      accessToken:
        'pk.eyJ1Ijoib3BlbnJlc2VhcmNoIiwiYSI6ImNrcDU2cXhhczFyMHIycG13eGQ1enl6ajgifQ.yIruwhwKAxueP8JJUHYrKQ',
    }),
  ],
};
